import React, { useRef, useState } from 'react';
import ContactModal from 'src/components/ContactModal';
import { DriverModal, EtaModal } from 'src/components/EtaModal';
import LineItemsCard from 'src/components/LineItemsCard';
import { ShipmentNotifications } from 'src/components/Notifications';
import { ShipmentBanner } from 'src/components/ShipmentBanner';
import StatusProgress from 'src/components/StatusProgress';
import {
  AddressCard,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Icon,
  Link,
  Row,
  toast,
  useApp
} from 'straightline-ui';
import {
  DOCUMENT_TYPE,
  ENTITY_TYPE,
  isCancelled,
  isDelivered,
  isDelivery,
  isMatched,
  isTransit,
  STATUS,
  statusType
} from 'straightline-utils/constants';
import { formatDistanceToNow } from 'straightline-utils/time';

const statusTexts = {
  [STATUS.BOOKED]: `The shipper booked the shipment.`,
  [STATUS.MATCHED]: `You have officially been assigned to this shipment! Dispatch a driver to continue. The driver will then be able to view the load details, tracking information, and more in the Straightline Driver app. You will also be able to download your Rate Confirmation once a driver is dispatched.`,
  [STATUS.DISPATCHED]: `Your Rate Confirmation is now available for download! To continue the load, please update the pickup ETA or have your driver do so. Once you have arrived at the pickup, change the status to "Pickup".`,
  [STATUS.PICKUP]: `The shipper has been notified that you have arrived at the pickup location. The driver should collect the BOL from the shipper and load the shipment. Once loading is completed, change the status to "Transit"`,
  [STATUS.TRANSIT]: `The shipment is en route to the delivery location. Once you have arrived, change the status to "Delivery".`,
  [STATUS.DELIVERY]: `The shipper has been notified that you have arrived at the delivery location. Once unloading is completed, the driver should change the status to "Delivered" and upload the POD.`,
  [STATUS.DELIVERED]: `Congratulations! The shipment has been successfully delivered. If you haven't already done so, please upload the POD. You can also download the Rate Confirmation and view the billing details.`,
  [STATUS.CANCELLED]: `The shipment has been cancelled. Please contact Straightline if you have any questions or concerns.`
};

function ChangeStatus({ shipment, onError, disabled }) {
  const app = useApp();
  const [open, setOpen] = useState(false);
  const changeOptions = shipment.status_change_options.map((option) =>
    statusType(option)
  );
  const currentStatus = statusType(shipment.status_type);

  const handleClick = (status) => {
    toast.confirm({
      title: 'Update Status',
      message: (
        <div>
          <p>Are you sure you want to set the status to "{status.label}"?</p>
          <div className="p-3 bg-light rounded">
            {statusTexts[status.value]}
          </div>
        </div>
      ),
      okButtonText: 'Yes, update status',
      cancelButtonText: 'Nevermind, do not update',
      handleOnOk: () => {
        return app
          .service('api/shipments')
          .patch(shipment.id, { status_type: status.value })
          .then(() => {
            toast.success(`Load ${shipment.id} is "${status.label}"`);
            app.service('api/shipments').invalidateGet(shipment.id);
            app.service('api/shipments').invalidateFind();

            if (status.value === STATUS.TRANSIT) {
              setOpen(true);
            }
          })
          .catch((error) => {
            toast.errors(error);
            if (onError) {
              onError(error);
            }
          });
      }
    });
  };

  return (
    <CardBody className="border-bottom px-3 p-md-4">
      <Row className="justify-content-between align-items-center mb-2">
        <Col sm="auto" className="mb-3">
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type="activity" className="mr-3" />
            Load Status
          </div>
        </Col>
        <Col sm="auto" className="mb-3">
          <StatusProgress
            shipment={shipment}
            onClick={disabled ? undefined : handleClick}
            unshift
            trim
          />
        </Col>
      </Row>
      <div>
        {statusTexts[currentStatus.value]}
        {changeOptions.length > 0 && (
          <React.Fragment>
            {' '}
            Click{' '}
            {changeOptions.length === 1 ? (
              <span
                onClick={() => handleClick(changeOptions[0])}
                className="text-primary pointer"
              >
                {changeOptions[0].label}
              </span>
            ) : (
              changeOptions.map((option, index) => (
                <span key={index}>
                  {index === 0 ? '' : ', or '}
                  <span
                    onClick={() => handleClick(option)}
                    className="text-primary pointer"
                  >
                    {option.label}
                  </span>
                </span>
              ))
            )}{' '}
            to update to the latest status.
          </React.Fragment>
        )}
      </div>
      <EtaModal
        shipment={shipment}
        open={open}
        toggle={() => setOpen(!open)}
        onComplete={() => setOpen(!open)}
      />
    </CardBody>
  );
}

const NotAvailable = () => (
  <span className="text-secondary">Not available</span>
);

export function ShipmentDispatch({ shipment }) {
  const [open, setOpen] = useState(false);
  const { shipment_dispatch } = shipment;

  return (
    <CardBody className="border-bottom px-3 p-md-4">
      <Row className="align-items-center justify-content-between mb-3">
        <Col xs="auto">
          <div
            className={`h3 mb-0 d-flex align-items-center ${
              shipment_dispatch ? '' : 'text-danger'
            }`}
          >
            <Icon type="user" className="mr-3" />
            Driver
            {!shipment_dispatch && (
              <Badge color="soft-danger" className="ml-3">
                Attention
              </Badge>
            )}
          </div>
        </Col>
        <Col xs="auto">
          <Button color="link" onClick={() => setOpen(true)}>
            {shipment_dispatch ? 'Update Driver' : 'Dispatch Driver'}
          </Button>
        </Col>
      </Row>
      <div>
        {shipment_dispatch ? (
          <div className="mb-3 d-flex">
            <div
              className="border rounded d-flex align-items-center justify-content-center mr-3 bg-primary-soft"
              style={{
                height: 45,
                width: 45
              }}
            >
              <Icon
                type="check-circle"
                className="text-primary font-weight-bold"
                style={{ fontSize: 20 }}
              />
            </div>
            <div>
              <div>
                {shipment_dispatch.carrier_driver ? (
                  <div
                    className="text-primary font-weight-bold pointer d-inline-block"
                    onClick={() => setOpen(true)}
                  >
                    {shipment_dispatch.carrier_driver}
                  </div>
                ) : (
                  <NotAvailable />
                )}
              </div>
              <div>
                <Icon type="truck" className="text-secondary mr-3" />
                {shipment_dispatch.carrier_truck_number || <NotAvailable />}
                <Icon type="book" className="text-secondary mx-3" />
                {shipment_dispatch.carrier_trailer_number || <NotAvailable />}
              </div>
            </div>
          </div>
        ) : (
          <div className="font-weight-bold text-danger mb-3">
            You cannot download the rate confirmation until you have dispatched
            this load.
          </div>
        )}
        Dispatch this load by assigning a driver name, phone, and truck details.
        Straightline will send the driver a text message with the load details
        and access to the Straightline Driver App.
      </div>
      <DriverModal
        shipment={shipment}
        open={open}
        toggle={() => setOpen(!open)}
        onComplete={() => setOpen(!open)}
      />
    </CardBody>
  );
}

function ShipmentTransfer({ shipment }) {
  const app = useApp();

  const { result: shipmentTransfer } = app
    .service('api/shipment-transfers')
    .useFindOne(
      {
        query: {
          shipment_id: shipment.id,
          $sort: { created_at: -1 }
        }
      },
      {
        keepPreviousData: true
      }
    );

  const disabled =
    !shipment.shipment_dispatch?.carrier_driver || !shipmentTransfer;

  return (
    <CardBody className="border-bottom px-3 p-md-4">
      <Row className="align-items-center justify-content-between mb-3">
        <Col xs="auto">
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type="dollar-sign" className="mr-3" />
            Rate Confirmation
          </div>
        </Col>
        <Col xs="auto">
          <Button
            color="link"
            tag="a"
            disabled={disabled}
            href={shipmentTransfer?.secure_url + '?download=true'}
          >
            Download
          </Button>
        </Col>
      </Row>
      {disabled && (
        <div className="font-weight-bold text-danger mb-3">
          You cannot download the rate confirmation until you have dispatched
          this load.
        </div>
      )}
      <div>
        Download the Rate Confirmation for this load to review all terms and
        conditions. Be sure to verify the pickup and delivery details and any
        special instructions provided by the shipper.
      </div>
    </CardBody>
  );
}

function ShipmentETA({ shipment }) {
  const [open, setOpen] = useState(false);
  const etaType = shipment.status.order <= 3 ? 'pickup' : 'delivery';
  const etaAddress = shipment[`${etaType}_address`];
  const etaLabel = etaType === 'pickup' ? 'Pickup' : 'Delivery';
  const { eta } = shipment;
  const needsAttention = !eta || eta.type !== etaType;

  if (isDelivery(shipment)) {
    return null;
  }

  return (
    <CardBody className="border-bottom px-3 p-md-4">
      <Row className="align-items-center justify-content-between mb-3">
        <Col xs="auto">
          <div
            className={`h3 mb-0 d-flex align-items-center ${
              needsAttention ? 'text-danger' : ''
            }`}
          >
            <Icon type="clock" className="mr-3" />
            <span className="d-none d-sm-inline">Estimated {etaLabel}</span>
            <span className="d-sm-none">{etaLabel} ETA</span>
            {needsAttention && (
              <Badge color="soft-danger" className="ml-3">
                Attention
              </Badge>
            )}
          </div>
        </Col>
        <Col xs="auto">
          <Button color="link" onClick={() => setOpen(true)}>
            Update ETA
          </Button>
        </Col>
      </Row>
      {eta?.type === etaType && (
        <div className="mb-3 d-flex">
          <div
            className="border rounded d-flex align-items-center justify-content-center mr-3 bg-primary-soft"
            style={{
              height: 45,
              width: 45
            }}
          >
            <Icon
              type="check-circle"
              className="text-primary font-weight-bold"
              style={{ fontSize: 20 }}
            />
          </div>
          <div>
            <div
              className="text-primary font-weight-bold pointer d-inline-block"
              onClick={() => setOpen(true)}
              style={{
                lineHeight: 1
              }}
            >
              {new Date(eta.date).toLocaleDateString('en-US', {
                timeZone: etaAddress.timezone,
                timeZoneName: 'short',
                hour: 'numeric',
                minute: 'numeric',
                month: 'long',
                weekday: 'long',
                day: 'numeric'
              })}
            </div>
            <div className="text-secondary">
              last updated {formatDistanceToNow(eta.created_at)}
            </div>
          </div>
        </div>
      )}
      {etaType === 'pickup' ? (
        <div>
          Set a date and time that you estimate to arrive in {etaAddress.city},{' '}
          {etaAddress.state} for pickup. You can update this ETA anytime and the
          shipper will be notified of your changes instantly.
        </div>
      ) : (
        <div>
          Set an estimate for the shipment to be delivered in {etaAddress.city},{' '}
          {etaAddress.state}. Shippers will be notified of your new ETA and may
          contact you with additional information.
        </div>
      )}
      <EtaModal
        shipment={shipment}
        open={open}
        toggle={() => setOpen(!open)}
        onComplete={() => setOpen(!open)}
      />
    </CardBody>
  );
}

function ShipmentPickupNumber({ shipment }) {
  return (
    <CardBody className="border-bottom px-3 p-md-4">
      <Row className="align-items-center justify-content-between mb-3">
        <Col xs="auto">
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type="bookmark" className="mr-3" />
            <span className="d-none d-sm-inline">Pickup Number</span>
            <span className="d-sm-none">Pickup #</span>
          </div>
        </Col>
        <Col xs="auto">
          <div className="h3 mb-0">
            {shipment.reference_number ? (
              <span className="text-primary">{shipment.reference_number}</span>
            ) : (
              <span className="text-secondary">Not available</span>
            )}
          </div>
        </Col>
      </Row>
      <div>
        A pickup number may be provided by the shipper and should be given to
        the driver. Some shipments may not have a pickup number, in which case
        the driver can provide the Straightline load number.
      </div>
    </CardBody>
  );
}

function ShipmentPOD({ shipment }) {
  const app = useApp();
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);

  const handleClick = () => {
    inputRef.current.value = null;
    inputRef.current.click();
  };

  const handleImageChange = (event) => {
    setIsLoading(true);
    const files = Array.from(event.target.files);
    return Promise.all(
      files.map((file) => {
        const data = {
          file,
          name: `Shipment #${shipment.id} POD`,
          document_type: DOCUMENT_TYPE.PROOF_OF_DELIVERY
        };

        return app
          .service('api/documents')
          .create(data)
          .then((document) => {
            return app.service('api/shipment-documents').create({
              shipment_id: shipment.id,
              document_id: document.id
            });
          })
          .catch((err) => toast.errors(err));
      })
    ).then(async () => {
      if (!isDelivered(shipment)) {
        try {
          if (isTransit(shipment)) {
            await app
              .service('api/shipments')
              .patch(shipment.id, { status_type: STATUS.DELIVERY });
          }
          await app
            .service('api/shipments')
            .patch(shipment.id, { status_type: STATUS.DELIVERED });
          app.service('api/shipments').invalidateFind();
          app.service('api/shipments').invalidateGet(shipment.id);
        } catch (error) {}
      }
      toast.success('POD uploaded successfully');
      app.service('api/shipment-documents').invalidateFind();
      app.service('api/shipments').invalidateGet(shipment.id);
      app.service('api/shipments').invalidateFind();
      setIsLoading(false);
    });
  };

  const { result } = app.service('api/shipment-documents').useFind(
    {
      query: {
        $limit: 1,
        $sort: { created_at: -1 },
        shipment_id: shipment.id,
        '$document.entity_type$': ENTITY_TYPE.CARRIER,
        '$document.document_type$': DOCUMENT_TYPE.PROOF_OF_DELIVERY
      }
    },
    { keepPreviousData: true }
  );

  if (!result) {
    return (
      <CardBody className="border-bottom px-3 p-md-4">
        <Row className="align-items-center justify-content-between mb-3">
          <Col xs="auto">
            <div className="h3 mb-0 d-flex align-items-center">
              <Icon type="clock" className="mr-3" />
              <span className="d-none d-sm-inline">Proof of Delivery</span>
              <span className="d-sm-none">POD</span>
            </div>
          </Col>
          <Col xs="auto">
            <Button color="link" disabled>
              Upload POD
            </Button>
          </Col>
        </Row>
        <div>
          Choose a file to upload as proof of delivery for this shipment. The
          file should be a signed BOL or POD from the receiver. Please ensure
          the file is clear and legible.
        </div>
      </CardBody>
    );
  }

  const podDocument = result.data[0];
  const isRequired =
    !podDocument &&
    [STATUS.DELIVERED, STATUS.DELIVERY].includes(shipment.status_type);

  return (
    <CardBody className="border-bottom px-3 p-md-4">
      <input
        type="file"
        accept=".jpg, .png, .pdf, .jpeg"
        multiple
        className="d-none"
        ref={inputRef}
        onChange={handleImageChange}
      />
      <Row className="align-items-center justify-content-between mb-3">
        <Col xs="auto">
          <div
            className={`h3 mb-0 d-flex align-items-center ${
              isRequired ? 'text-danger' : ''
            }`}
          >
            <Icon type="file-text" className="mr-3" />
            <span className="d-none d-sm-inline">Proof of Delivery</span>
            <span className="d-sm-none">POD</span>
            {isRequired && (
              <Badge color="soft-danger" className="ml-3">
                Attention
              </Badge>
            )}
            {!podDocument && !isRequired && (
              <Badge color="soft-secondary" className="ml-3">
                Waiting
              </Badge>
            )}
          </div>
        </Col>
        <Col xs="auto">
          <Button
            color="link"
            onClick={handleClick}
            loading={isLoading}
            disabled={isLoading}
          >
            Upload POD
          </Button>
        </Col>
      </Row>
      {podDocument && (
        <div className="mb-3 d-flex">
          <div
            className="border rounded d-flex align-items-center justify-content-center mr-3 bg-primary-soft"
            style={{
              height: 45,
              width: 45
            }}
          >
            <Icon
              type="check-circle"
              className="text-primary font-weight-bold"
              style={{ fontSize: 20 }}
            />
          </div>
          <div>
            <div
              className="text-primary font-weight-bold d-inline-block"
              style={{
                lineHeight: 1
              }}
            >
              {podDocument.document.name}
            </div>
            <div className="text-secondary">
              uploaded {formatDistanceToNow(podDocument.created_at)}
            </div>
          </div>
        </div>
      )}
      <div>
        Choose a file to upload as proof of delivery for this shipment. The file
        should be a signed BOL or POD from the receiver. Please ensure the file
        is clear and legible.
      </div>
    </CardBody>
  );
}

function ShipmentServiceCard({ shipment }) {
  return (
    <Card>
      <CardBody className="px-3 p-md-4">
        <div className="mb-4">
          <ShipmentBanner shipment={shipment} />
        </div>
        <div className="mb-4">
          <h5 className="text-uppercase text-muted font-weight-bold">
            Service
          </h5>
          <div>{shipment.rate.service_level.name}</div>
          <div>{shipment.rate.service_level.description}</div>
          {shipment.rate.service_level.code === 'TEM' && (
            <div className="font-weight-bold text-primary">
              ** This shipment requires two drivers **
            </div>
          )}
        </div>
        <div>
          <h5 className="text-uppercase text-muted font-weight-bold">Notes</h5>
          <div className="pre-line">
            {shipment.notes || (
              <span className="text-muted">Not available</span>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

export default function ShipmentDetails({ shipment }) {
  if (isCancelled(shipment)) {
    return (
      <Row>
        <Col xl="8">
          <Card>
            <CardBody className="border-bottom px-3 p-md-4">
              <Row className="justify-content-between align-items-center mb-2">
                <Col sm="auto" className="mb-3">
                  <div className="h3 mb-0 d-flex align-items-center">
                    <Icon type="activity" className="mr-3" />
                    Load Status
                  </div>
                </Col>
                <Col sm="auto" className="mb-3">
                  <div className="progress progress-status">
                    <div
                      className="bg-danger progress-bar"
                      style={{ width: '100%' }}
                    >
                      Cancelled
                    </div>
                  </div>
                </Col>
              </Row>
              <div>
                This load has been cancelled. The shipper likely cancelled
                because the load was not ready or needed to be picked up on a
                different date. If you have any questions, please{' '}
                <ContactModal
                  config={{
                    subject: `App Contact - Shipment #${shipment.id}`,
                    service: 'api/shipments',
                    service_id: shipment.id
                  }}
                >
                  {(toggle) => (
                    <span className="text-primary pointer" onClick={toggle}>
                      Contact Straightline
                    </span>
                  )}
                </ContactModal>
                .
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  }

  if (isDelivered(shipment)) {
    return (
      <Row>
        <Col xl="7">
          <Card>
            <CardBody className="border-bottom px-3 p-md-4">
              <Row className="justify-content-between align-items-center mb-2">
                <Col sm="auto" className="mb-3">
                  <div className="h3 mb-0 d-flex align-items-center">
                    <Icon type="activity" className="mr-3" />
                    Load Status
                  </div>
                </Col>
                <Col sm="auto" className="mb-3">
                  <StatusProgress
                    shipment={shipment}
                    // onClick={handleClick}
                    unshift
                    trim
                  />
                </Col>
              </Row>
              <div>
                This load has been successfully delivered! Upload the POD
                document to finalize the shipment and initiate payment. Check
                the <Link to="/shipment-matches">Load Board</Link> for shipments
                near {shipment.delivery_address.city} or{' '}
                <Link to="/shipment-request">Post a Truck</Link> to find your
                next load.
              </div>
            </CardBody>
            <ShipmentRequest shipment={shipment} />
            <ShipmentTransfer shipment={shipment} />
            <ShipmentPOD shipment={shipment} />
          </Card>
          <Card>
            <CardBody className="px-3 p-md-4">
              <ShipmentNotifications shipment={shipment} />
            </CardBody>
          </Card>
          <ShipmentServiceCard shipment={shipment} />
          <LineItemsCard shipment={shipment} />
        </Col>
        <Col xl="5">
          <AddressCard shipment={shipment} type="pickup" />
          <AddressCard shipment={shipment} type="delivery" />
        </Col>
      </Row>
    );
  }

  return (
    <React.Fragment>
      <Row>
        <Col xl="7">
          <Card>
            <ShipmentPickupNumber shipment={shipment} />
            <ChangeStatus shipment={shipment} disabled={isMatched(shipment)} />
            <ShipmentDispatch shipment={shipment} />
            <div className={isMatched(shipment) ? 'd-none' : ''}>
              <ShipmentTransfer shipment={shipment} />
              <ShipmentETA shipment={shipment} />
              <ShipmentPOD shipment={shipment} />
            </div>
          </Card>
          <Card>
            <CardBody className="px-3 p-md-4">
              <ShipmentNotifications shipment={shipment} />
            </CardBody>
          </Card>
          <ShipmentServiceCard shipment={shipment} />
          <LineItemsCard shipment={shipment} />
        </Col>
        <Col xl="5">
          <AddressCard shipment={shipment} type="pickup" />
          <AddressCard shipment={shipment} type="delivery" />
        </Col>
      </Row>
      {/*
      <Card>
        <div className="card-header">
          <div className="d-flex justify-content-between">
            <h3 className="card-header-title">Load Details</h3>
            <h3 className="card-header-title text-success">
              {formatMoney(shipment.rate.price.carrier_price)}
            </h3>
          </div>
        </div>
        <CardBody>
          <Row>
            <Col xl="6" sm="6" className="mb-4">
              <h5 className="text-uppercase text-muted font-weight-bold">
                Truck Type
              </h5>
              <p className="mb-0">{shipment.truck.label}</p>
            </Col>
            <Col xl="6" sm="6" className="mb-4">
              <h5 className="text-uppercase text-muted font-weight-bold">
                Weight
              </h5>
              <p className="mb-0">
                {shipment.load_weight?.toLocaleString() || 'NA'} lbs
              </p>
            </Col>
          </Row>
          <h5 className="text-uppercase text-muted font-weight-bold">Notes</h5>
          <p className="pre-line mb-0">{shipment.notes}</p>
        </CardBody>
      </Card> */}
    </React.Fragment>
  );
}

function ShipmentRequest({ shipment }) {
  const app = useApp();
  const [isPostingTruck, setIsPostingTruck] = useState(false);

  const { result: shipmentRequest } = app
    .service('api/shipment-requests')
    .useFindOne(
      {
        query: {
          shipment_id: shipment.id,
          carrier_id: app.auth.user.carrier_id
        }
      },
      {
        keepPreviousData: true
      }
    );

  const handlePostTruck = () => {
    setIsPostingTruck(true);
    return app
      .service('api/shipment-requests')
      .create({
        carrier_id: app.auth.user.carrier_id,
        carrier_user_id: app.auth.user.id,
        truck_types: [shipment.truck_type],
        active: true,
        pickup_address: {
          ...shipment.pickup_address,
          radius: 150
        }
      })
      .then(() => {
        app.service('api/shipment-requests').invalidateFind();
        app.service('api/shipments').invalidateGet(shipment.id);
        app.service('api/shipments').invalidateFind();
        toast.success(
          `You have successfully posted a truck in ${shipment.pickup_address.city}, ${shipment.pickup_address.state}. You will receive notifications when new loads are available on this lane.`
        );
      })
      .catch((error) => {
        setIsPostingTruck(false);
        toast.errors(error);
      });
  };

  if (shipmentRequest) {
    return (
      <CardBody className="border-bottom px-3 p-md-4">
        <Row className="justify-content-between align-items-center mb-2">
          <Col sm="auto" className="mb-3">
            <div className="h3 mb-0 d-flex align-items-center">
              <Icon type="truck" className="mr-3" />
              Find Another Load
            </div>
          </Col>
          <Col sm="auto" className="mb-3">
            <Button color="link" to="/shipment-matches">
              Search Loads
            </Button>
          </Col>
        </Row>
        <div>
          You have posted a truck in {shipment.pickup_address.city},{' '}
          {shipment.pickup_address.state}. You will receive notifications when
          new loads are available on this lane! Manage your posted trucks in the{' '}
          <Link to="/shipment-requests">Posted Trucks</Link> page.
        </div>
      </CardBody>
    );
  }

  return (
    <CardBody className="border-bottom px-3 p-md-4">
      <Row className="justify-content-between align-items-center mb-1">
        <Col sm="auto" className="mb-3">
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type="truck" className="mr-3" />
            Find Another Load
          </div>
        </Col>
        <Col sm="auto" className="mb-3">
          <Button color="link" to="/shipment-matches">
            Search Loads
          </Button>
          <Button
            color="link"
            onClick={handlePostTruck}
            isLoading={isPostingTruck}
            disabled={isPostingTruck}
          >
            Post Truck
          </Button>
        </Col>
      </Row>
      <div>
        Post a truck in {shipment.pickup_address.city},{' '}
        {shipment.pickup_address.state} to receive notifications when new loads
        are available on this lane. Posted trucks are the best way to get
        matched with new loads and win more business. Manage your posted trucks
        in the <Link to="/shipment-requests">Posted Trucks</Link> page.
      </div>
    </CardBody>
  );
}
